import {
  MockEventButton,
  type IMockEventOption
} from '../MockEventButton/MockEventButton';
import { emitMockSocketEvent } from '@/services/mercure/mockSocketEvents';
import {
  buildMockCallEvent,
  type TCallMockScenario
} from '@/utils/buildMockCallEvent';
import { isCallMockAllowed } from '@/utils/isCallMockAllowed';

const BUTTON_LABEL = 'Тест звонка';
const BUTTON_LABEL_SHORT = 'Звонок';
const PANEL_TITLE = 'Эмуляция события звонка';

/** Сколько кадров шлёт залповый сценарий за одно нажатие. */
const BURST_SIZE = 10;

/**
 * Сколько кадров даёт сценарий. Залп отличается от одиночного звонка только
 * количеством отправок — сам кадр строит та же утилита.
 */
const getEventsCount = (scenario: TCallMockScenario): number =>
  scenario === 'incomingBurst' ? BURST_SIZE : 1;

const SCENARIOS: readonly IMockEventOption<TCallMockScenario>[] = [
  {
    id: 'incoming',
    label: 'Входящий — аккаунт найден',
    hint: 'INCOMING с привязанным аккаунтом: тост, звук, модалка с заказами'
  },
  {
    id: 'incomingUnknown',
    label: 'Входящий — аккаунт не найден',
    hint: 'INCOMING с user: null и номером 79183221130 — модалка без карточки аккаунта, но с табом «Заказы по номеру»'
  },
  {
    id: 'incomingPartial',
    label: 'Входящий — неполные данные аккаунта',
    hint: 'user только с id, без телефона и имени: событие не должно потеряться'
  },
  {
    id: 'incomingNewNumber',
    label: 'Входящий — новый номер',
    hint: 'Каждое нажатие даёт новый номер: карточки не схлопываются, стек скроллится'
  },
  {
    id: 'incomingBurst',
    label: 'Входящих сразу 10',
    hint: 'Залп из 10 разных номеров; каждое нажатие добавляет ещё 10 карточек'
  },
  {
    id: 'accepted',
    label: 'Принят — гасит тост',
    hint: 'ACCEPTED тем же номером: карточка входящего должна исчезнуть'
  },
  {
    id: 'acceptedAhead',
    label: 'Принят «из будущего»',
    hint: 'ACCEPTED со временем +60 с: следующий входящий не должен всплыть'
  },
  {
    id: 'missed',
    label: 'Пропущенный из истории',
    hint: 'account_calls_persist + status: missed — карточка другого оттенка'
  },
  {
    id: 'missedAhead',
    label: 'Пропущенный «из будущего»',
    hint: 'missed со временем +60 с: следующий входящий не всплывает и не звенит'
  },
  {
    id: 'historySuccess',
    label: 'История: Success',
    hint: 'Тоста нет, но карточка входящего гаснет и номер уходит в реестр'
  }
];

/**
 * Тестовая кнопка эмуляции события звонка. Видна только при включённом флаге
 * VITE_CALL_MOCK_ALLOW. Отправляет сырой кадр в общий канал эмуляции, откуда
 * он проходит тот же разбор, что и настоящее событие Mercure.
 */
export const CallMockButton: React.FC = () => {
  if (!isCallMockAllowed()) {
    return null;
  }

  const handleSelect = (scenario: TCallMockScenario): void => {
    for (let index = 0; index < getEventsCount(scenario); index += 1) {
      emitMockSocketEvent(buildMockCallEvent(scenario));
    }
  };

  return (
    <MockEventButton
      label={BUTTON_LABEL}
      shortLabel={BUTTON_LABEL_SHORT}
      title={PANEL_TITLE}
      options={SCENARIOS}
      onSelect={handleSelect}
    />
  );
};
